/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.viewmodel

import systems.frictionless.impulse.rest.client.model.RestResponse

/**
 * Represents a state transition for doing requests to the server.
 *
 * 1. ViewModel starts in Idle state.
 * 2. ViewModel transitions to Busy state while request is performed.
 * 3. If request succeeds, transitions to Complete state containing the result.
 *    If request fails, transitions to Failure state containing the builder (so that UI can repopulate) and failure.
 *
 * @param BuilderType The type used to perform network requests.
 * @param ResponseType The type returned from the server.
 * @param ResultType The type emitted by the ViewModel once work is complete.  Often this is the same as ResponseType.
 */
sealed class NetworkViewModelState<BuilderType, ResponseType, ResultType> {
    class Idle<BuilderType, ResponseType, ResultType> :
        NetworkViewModelState<BuilderType, ResponseType, ResultType>()

    class Busy<BuilderType, ResponseType, ResultType> : NetworkViewModelState<BuilderType, ResponseType, ResultType>()

    data class Failure<BuilderType, ResponseType, ResultType>(
        val builder: BuilderType,
        val failure: RestResponse.Failure<ResponseType>
    ) : NetworkViewModelState<BuilderType, ResponseType, ResultType>()

    data class Complete<BuilderType, ResponseType, ResultType>(val result: ResultType) :
        NetworkViewModelState<BuilderType, ResponseType, ResultType>()
}
